import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EMPTY_SEARCH, searchAll, type SearchEntityKind, type SearchResult } from "../lib/searchApi.ts";
import { ApiError } from "../lib/api.ts";
import { currentLang, t, useLang, type L } from "../i18n/lang.ts";
import { BADGE, SCREEN, SCREEN_ACCESS } from "../i18n/dict.ts";
import { money, num } from "../lib/format.ts";

/**
 * Поиск по системе.
 *
 * Заказчик сформулировал так: «У нас до сих пор нет поиска по системе. Поиск по
 * справочнику не считается, нужен именно поиск по аналитике и метрикам».
 *
 * ЧЕТЫРЕ НАМЕРЕНИЯ, И ОНИ РАЗЛИЧАЮТСЯ ТЕМ, ЧТО ПРОИСХОДИТ ПОСЛЕ ВЫБОРА.
 * Показатель — перейти на экран И прочитать определение. Сущность (парк, канал,
 * стадия, кампания) — перейти на экран с УЖЕ НАЛОЖЕННЫМ фильтром. Конкретная
 * запись — открыть карточку. Настройка — попасть в администрирование. Поиск,
 * который на все четыре вопроса отвечает одинаковым списком ссылок, — не поиск,
 * а оглавление.
 *
 * ДВУЯЗЫЧИЕ ЗДЕСЬ НЕ УКРАШЕНИЕ, А ГЛАВНОЕ. Интерфейс двуязычный, а память у
 * человека одна: он помнит показатель как «ROMI» или как «Реклама / выручка», и
 * заранее не знает, на каком языке смотрит система. Поэтому поиск идёт по ОБОИМ
 * языкам всегда, а найденное печатается на текущем — с подписью на том языке,
 * которым совпало, если он другой. Ровно этого не умеет поиск по справочнику
 * (`components/reference.tsx`): он фильтрует по `blockText(b, lang)`, то есть
 * по одному текущему языку, и при английском интерфейсе «Реклама / выручка» не
 * находится ничем. Повторять этот дефект нельзя.
 *
 * ИНДЕКС ПОКАЗАТЕЛЕЙ НЕ ПИШЕТСЯ РУКАМИ. Он строится из того же объекта, из
 * которого рисуется справочник (`content/metricsReference.ts`), и из того же
 * словаря, из которого рисуется меню (`i18n/dict.ts`). Список подписей,
 * набранный здесь заново, разошёлся бы с ними при первой правке — и разошёлся бы
 * молча.
 *
 * ПОЧЕМУ НЕ АДРЕСНАЯ СТРОКА. Экран в этом приложении — состояние React, а не
 * маршрут. Поиск отдаёт результат ОБРАТНЫМ ВЫЗОВОМ (`SearchNav`), потому что
 * ввести маршрутизацию ради поиска значило бы переписать навигацию всего
 * приложения — цена, несопоставимая с задачей.
 */

const TX = {
  open: { ru: "Поиск по системе", en: "Search the system" },
  placeholder: {
    ru: "Показатель, парк, канал, стадия, лид, телефон…",
    en: "A metric, a park, a channel, a stage, a lead, a phone…",
  },
  tooShort: {
    ru: "Наберите хотя бы два знака",
    en: "Type at least two characters",
  },
  recordsNeedThree: {
    ru: "Лиды ищутся с трёх знаков",
    en: "Leads are searched from three characters",
  },
  nothing: { ru: "Ничего не найдено", en: "Nothing found" },
  nothingHint: {
    ru: "Проверьте раскладку: поиск идёт и по русским, и по английским названиям, но не по опечаткам.",
    en: "Check the keyboard layout: the search covers both Russian and English names, but not typos.",
  },
  searching: { ru: "Ищем…", en: "Searching…" },
  hintKeys: { ru: "↑↓ выбрать · Enter открыть · Esc закрыть", en: "↑↓ move · Enter open · Esc close" },

  /* --- заголовки разделов */
  secScreens: { ru: "Экраны", en: "Screens" },
  secMetrics: { ru: "Показатели и определения", en: "Metrics and definitions" },
  secEntities: { ru: "Справочники", en: "Reference data" },
  secRecords: { ru: "Записи", en: "Records" },
  secSettings: { ru: "Настройки и источники", en: "Settings and data sources" },

  /* --- виды сущностей */
  kindPark: { ru: "парк", en: "park" },
  kindBrand: { ru: "бренд", en: "brand" },
  kindChannel: { ru: "канал", en: "channel" },
  kindStage: { ru: "стадия", en: "stage" },
  kindManager: { ru: "менеджер", en: "salesperson" },
  kindCampaign: { ru: "кампания", en: "campaign" },
  kindScreen: { ru: "экран", en: "screen" },
  kindSection: { ru: "раздел справочника", en: "reference section" },
  kindSetting: { ru: "настройка", en: "setting" },
  kindSource: { ru: "источник данных", en: "data source" },
  kindLead: { ru: "лид", en: "lead" },

  /* --- что произойдёт по выбору. Печатается в строке: человек должен знать,
         куда его унесёт, ДО нажатия. */
  actScreen: { ru: "открыть экран", en: "open the screen" },
  actReference: { ru: "открыть определение", en: "open the definition" },
  actParkFilter: { ru: "оставить только этот парк", en: "filter to this park only" },
  actLeadList: { ru: "показать лиды списком", en: "show the leads as a list" },
  actBitrix: { ru: "открыть карточку в Битрикс24", en: "open the card in Bitrix24" },
  actLookup: { ru: "искать этого человека в Client lookup", en: "look this person up in Client lookup" },

  /* --- достоверность показателя. Печатается прямо в строке результата:
         человек, спросивший «где ROMI», обязан узнать из ответа, что это
         ОЦЕНКА, а не измерение. Ждать, пока он откроет справочник и дочитает,
         — значит позволить ему сослаться на оценку как на факт. */

  /* --- оговорки */
  inactive: { ru: "неактивно", en: "inactive" },
  listUsesPeriod: {
    ru: "Список откроется по выбранному сверху периоду",
    en: "The list opens under the period selected above",
  },
  moreOfKind: { ru: "ещё", en: "and more" },
  totalLeads: { ru: "подошло лидов", en: "leads match" },
  noRightNote: {
    ru: "Лиды, имена и телефоны не показываются: на это нужно право Client lookup.",
    en: "Leads, names and phone numbers are not shown: that needs the Client lookup right.",
  },
  matchedOther: { ru: "совпало", en: "matched" },
} satisfies Record<string, L>;

/* -------------------------------------------------------------------- типы */

/** Срез списка лидов. Ровно те поля, что есть в публичном `LeadSlice`. */
export type SearchDrill = {
  title: string;
  hint?: string;
  slice: { sourceGroup?: string; parkKey?: string; stageId?: string; search?: string };
};

/**
 * Что поиск умеет сделать с приложением.
 *
 * Три действия, и больше не нужно: сменить экран, наложить фильтр парков,
 * открыть список лидов срезом. Справочник расчётов открывается первым же
 * действием (`goScreen("metrics")`), а прокрутку к нужному разделу поиск делает
 * сам — идентификаторы у разделов уже есть, и переход по ним уже работает в
 * оглавлении справочника.
 */
export type SearchNav = {
  goScreen: (screen: string) => void;
  setParks: (parkKeys: string[]) => void;
  openLeads: (d: SearchDrill) => void;
};

type Badge = "exact" | "estimate" | "ratio";

type Group = "screens" | "metrics" | "entities" | "records" | "settings";

type Item = {
  id: string;
  group: Group;
  /** Подпись на ТЕКУЩЕМ языке. */
  label: string;
  /** Подпись на другом языке — печатается, только если совпало именно ей. */
  alt: string | null;
  hint: string | null;
  kind: L;
  action: L;
  badge: Badge | null;
  inactive: boolean;
  run: () => void;
};

/** Один блок индекса показателей: заголовок раздела, показатель или выноска. */
type MetricEntry = {
  sectionId: string;
  name: L;
  badge?: Badge;
};

/* ------------------------------------------------------------ вспомогательное */

const BADGE_LABEL: Record<Badge, L> = {
  exact: BADGE.exact,
  estimate: BADGE.estimate,
  ratio: BADGE.ratio,
};

const ENTITY_KIND_LABEL: Record<SearchEntityKind, L> = {
  park: TX.kindPark,
  brand: TX.kindBrand,
  channel: TX.kindChannel,
  stage: TX.kindStage,
  manager: TX.kindManager,
  campaign: TX.kindCampaign,
};

/**
 * Право `journey_lookup` — отдельный ключ, не экран приложения: в меню оно
 * живёт вкладкой внутри Customer journey. Всё остальное совпадает с ключом
 * экрана один в один.
 */
const SCREEN_OF_RIGHT: Record<string, string> = { journey_lookup: "journey" };

function norm(s: string): string {
  return s.trim().toLowerCase();
}

/**
 * Совпадение по нескольким парам переводов сразу.
 *
 * Возвращает не только «да», но и ЧЕМ совпало: языком и самим текстом. Без
 * этого нельзя честно показать, почему строка попала в выдачу: человек ищет
 * «реклама», а интерфейс английский, и в результате «Ads / revenue» слова
 * «реклама» нет ни одного. Подпись «совпало: Реклама / выручка» превращает
 * загадку в объяснение.
 */
function findMatch(pairs: Array<L | null>, needle: string): { lang: "ru" | "en"; text: string } | null {
  for (const p of pairs) {
    if (p === null) continue;
    if (norm(p.en).includes(needle)) return { lang: "en", text: p.en };
    if (norm(p.ru).includes(needle)) return { lang: "ru", text: p.ru };
  }
  return null;
}

/**
 * Подсветка совпадения.
 *
 * Нужна не для красоты: в строке «Instagram post: A day in the life of Amr…»
 * без подсветки непонятно, чем именно она подошла, и результат выглядит
 * случайным.
 */
function Mark({ text, needle }: { text: string; needle: string }): React.ReactElement {
  if (needle === "") return <>{text}</>;
  const at = text.toLowerCase().indexOf(needle);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="gs-mark">{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/**
 * Прокрутка справочника к разделу.
 *
 * Справочник — отдельный чанк и грузится лениво, поэтому элемента раздела в
 * момент переключения экрана ещё нет. Ждём его появления, а не прокручиваем
 * вслепую. Потолок ожидания есть: без него неудачный переход крутил бы таймер
 * до конца сессии.
 */
function scrollToSection(id: string, tries = 40): void {
  const el = document.getElementById(id);
  if (el !== null) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  if (tries > 0) window.setTimeout(() => scrollToSection(id, tries - 1), 50);
}

/* --------------------------------------------------------------- компонент */

export function GlobalSearch({
  nav,
  canScreen,
}: {
  nav: SearchNav;
  /** Доступ к экрану. Та же функция, что скрывает пункт меню. */
  canScreen: (key: string) => boolean;
}): React.ReactElement {
  useLang();
  const lang = currentLang();

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [server, setServer] = useState<SearchResult>(EMPTY_SEARCH);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);

  /**
   * Индекс показателей грузится ЛЕНИВО, вместе с чанком справочника.
   *
   * Справочник вынесен в отдельный чанк намеренно: 52 КБ текста на двух языках
   * не должны задерживать первый экран у всех. Статический импорт ради поиска
   * вернул бы их в основную сборку и отменил бы то решение. Чанк тот же самый,
   * что открывает экран справочника, — второй раз он не скачивается.
   */
  const [metrics, setMetrics] = useState<MetricEntry[] | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  /**
   * Вызов по горячей клавише.
   *
   * Ctrl/Cmd+K, а не «/»: приложение состоит из полей ввода — даты, логин, имя
   * набора фильтров, — и одиночный знак перехватывал бы ввод в них. Escape
   * висит здесь же, а не только на поле ввода: после щелчка по строке фокус
   * уходит с поля, и панель перестала бы закрываться клавишей.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    inputRef.current?.select();
    if (metrics !== null) return;
    let alive = true;
    void import("../content/metricsReference.ts").then((m) => {
      if (!alive) return;
      const out: MetricEntry[] = [];
      for (const s of m.SECTIONS) {
        out.push({ sectionId: s.id, name: s.title });
        for (const b of s.blocks) {
          if (b.kind === "metric") out.push({ sectionId: s.id, name: b.name, badge: b.badge });
          else if (b.kind === "h3") out.push({ sectionId: s.id, name: b.text });
          else if (b.kind === "callout") out.push({ sectionId: s.id, name: b.title });
        }
      }
      setMetrics(out);
    });
    return () => {
      alive = false;
    };
  }, [open, metrics]);

  /* --- запрос к серверу: задержка и отмена */
  useEffect(() => {
    const needle = q.trim();
    if (!open || needle.length < 2) {
      setServer(EMPTY_SEARCH);
      setBusy(false);
      return;
    }
    const ctrl = new AbortController();
    /**
     * Задержка перед запросом. 220 мс — не вкус: замер полного поиска по слову
     * «mohammed» даёт 213 мс, из которых почти всё — список лидов
     * (`api/leads.ts`, счётчик по соединению трёх представлений). Запрос на
     * каждую букву без задержки означал бы восемь таких запросов на одно слово.
     */
    const timer = window.setTimeout(() => {
      setBusy(true);
      setError(null);
      searchAll(needle, ctrl.signal)
        .then((r) => setServer(r))
        .catch((e: unknown) => {
          if (ctrl.signal.aborted) return;
          setError(e instanceof ApiError ? e.message : String(e));
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setBusy(false);
        });
    }, 220);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [q, open]);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
  }, []);

  const needle = norm(q);

  /* ------------------------------------------------------------- сборка выдачи */

  const items = useMemo<Item[]>(() => {
    if (needle.length < 2) return [];
    const out: Item[] = [];

    /* --- экраны. Из общего словаря, оба языка, и только доступные. */
    for (const [key, s] of Object.entries(SCREEN)) {
      const screen = SCREEN_OF_RIGHT[key] ?? key;
      if (!canScreen(key)) continue;
      const access = (SCREEN_ACCESS as Record<string, L | undefined>)[key] ?? null;
      const hit = findMatch([s.title, s.sub, access], needle);
      if (hit === null) continue;
      out.push({
        id: `screen:${key}`,
        group: "screens",
        label: t(s.title),
        alt: hit.lang === lang || hit.text === t(s.title) ? null : hit.text,
        hint: t(s.sub),
        kind: TX.kindScreen,
        action: TX.actScreen,
        badge: null,
        inactive: false,
        run: () => {
          nav.goScreen(screen);
          close();
        },
      });
    }

    /* --- показатели и определения из единого словаря справочника */
    for (const [i, e] of (canScreen("metrics") ? (metrics ?? []) : []).entries()) {
      const hit = findMatch([e.name], needle);
      if (hit === null) continue;
      const shown = t(e.name);
      out.push({
        id: `metric:${i}`,
        group: "metrics",
        label: shown,
        alt: hit.text === shown ? null : hit.text,
        hint: null,
        kind: TX.kindSection,
        action: TX.actReference,
        badge: e.badge ?? null,
        inactive: false,
        run: () => {
          nav.goScreen("metrics");
          scrollToSection(e.sectionId);
          close();
        },
      });
    }

    /* --- справочники базы: ответ на выбор — экран с наложенным фильтром */
    for (const e of server.entities) {
      const item: Item = {
        id: `entity:${e.kind}:${e.key}`,
        group: "entities",
        label: e.label,
        alt: null,
        hint: e.hint,
        kind: ENTITY_KIND_LABEL[e.kind],
        action: TX.actScreen,
        badge: null,
        inactive: e.inactive,
        run: close,
      };
      /*
       * Действие зависит от вида, потому что фильтры у приложения разные.
       * Фильтр парков — настоящий фильтр в шапке, он применяется ко всем
       * экранам сразу. Канала и стадии в шапке нет, зато есть список лидов
       * ровно этим срезом — тот же, что открывается под плиткой. Для бренда,
       * менеджера и кампании ни фильтра, ни среза в публичном интерфейсе нет,
       * и выдумывать их здесь нельзя: остаётся экран, где эта сущность и
       * разложена по строкам.
       */
      if (e.kind === "park") {
        item.action = TX.actParkFilter;
        item.run = () => {
          nav.setParks([e.key]);
          close();
        };
      } else if (e.kind === "channel") {
        item.action = TX.actLeadList;
        item.hint = [e.hint, t(TX.listUsesPeriod)].filter((x) => x !== null && x !== "").join(" · ");
        item.run = () => {
          nav.openLeads({ title: e.label, hint: t(TX.actLeadList), slice: { sourceGroup: e.key } });
          close();
        };
      } else if (e.kind === "stage" && e.hint === "lead") {
        item.action = TX.actLeadList;
        item.hint = t(TX.listUsesPeriod);
        item.run = () => {
          nav.openLeads({ title: e.label, hint: t(TX.actLeadList), slice: { stageId: e.key } });
          close();
        };
      } else {
        const screen =
          e.kind === "brand" ? "brands"
          : e.kind === "manager" ? "pipeline"
          : e.kind === "campaign" ? "marketing"
          : "operations";
        item.run = () => {
          nav.goScreen(screen);
          close();
        };
      }
      out.push(item);
    }

    /* --- записи. Раздела нет вовсе, когда нет права. */
    if (server.records !== null) {
      for (const r of server.records.rows) {
        const parts = [
          r.phone_e164,
          r.park_name,
          r.stage_name,
          r.deal_id === null
            ? null
            : `${r.deal_stage_name ?? `#${r.deal_id}`}${
                r.deal_amount_aed_minor === null
                  ? ""
                  : ` · ${money(r.deal_amount_aed_minor, "AED", { withCode: false })}`
              }`,
          r.created,
        ].filter((x): x is string => x !== null && x !== "");
        out.push({
          id: `lead:${r.lead_id}`,
          group: "records",
          label: r.title === null || r.title === "" ? `#${r.lead_id}` : r.title,
          alt: null,
          hint: parts.join(" · "),
          kind: TX.kindLead,
          action: TX.actBitrix,
          badge: null,
          inactive: false,
          run: () => {
            if (r.bitrix_url !== null) window.open(r.bitrix_url, "_blank", "noopener");
            close();
          },
        });
      }
      /*
       * Ведём в Client lookup, а не повторяем его. Тот экран уже собирает
       * человека из всех его обращений и показывает основание решения об
       * источнике; делать это второй раз здесь значило бы завести второй ответ
       * на один вопрос. Пункт стоит последним в разделе — после конкретных
       * записей, потому что чаще нужна именно запись.
       */
      if (server.records.rows.length > 0 && canScreen("journey_lookup")) {
        out.push({
          id: "lookup",
          group: "records",
          label: t(TX.actLookup),
          alt: null,
          hint: `${num(server.records.total)} ${t(TX.totalLeads)}`,
          kind: TX.kindScreen,
          action: TX.actScreen,
          badge: null,
          inactive: false,
          run: () => {
            nav.goScreen("journey");
            close();
          },
        });
      }
      if (server.records.total > server.records.rows.length) {
        out.push({
          id: "leads-all",
          group: "records",
          label: `${num(server.records.total)} ${t(TX.totalLeads)}`,
          alt: null,
          hint: t(TX.listUsesPeriod),
          kind: TX.kindLead,
          action: TX.actLeadList,
          badge: null,
          inactive: false,
          run: () => {
            nav.openLeads({ title: q.trim(), hint: t(TX.actLeadList), slice: { search: q.trim() } });
            close();
          },
        });
      }
    }

    /* --- настройки и источники: только администратору */
    for (const s of server.settings ?? []) {
      out.push({
        id: `setting:${s.kind}:${s.key}`,
        group: "settings",
        label: s.label,
        alt: null,
        hint: s.hint,
        kind: s.kind === "setting" ? TX.kindSetting : TX.kindSource,
        action: TX.actScreen,
        badge: null,
        inactive: false,
        run: () => {
          nav.goScreen("admin");
          close();
        },
      });
    }

    return out;
  }, [needle, metrics, server, lang, canScreen, nav, close, q]);

  /** Курсор возвращается на первый пункт при смене выдачи: иначе он висит в пустоте. */
  useEffect(() => {
    setCursor(0);
  }, [items.length, needle]);

  /* --- клавиатурная навигация */
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c - 1 + items.length) % items.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setCursor(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setCursor(items.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      items[cursor]?.run();
    }
  };

  /** Выбранный пункт держим в виду: без этого стрелки уводят курсор за край. */
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [cursor, items.length]);

  /* ------------------------------------------------------------------ разметка */

  const groups: Array<{ key: Group; head: L }> = [
    { key: "screens", head: TX.secScreens },
    { key: "metrics", head: TX.secMetrics },
    { key: "entities", head: TX.secEntities },
    { key: "records", head: TX.secRecords },
    { key: "settings", head: TX.secSettings },
  ];

  const moreLine = Object.entries(server.entityMore)
    .map(([k, n]) => `${t(TX.moreOfKind)} ${num(n)} — ${t(ENTITY_KIND_LABEL[k as SearchEntityKind])}`)
    .join(" · ");

  return (
    <>
      <style>{CSS}</style>
      <button
        type="button"
        className="chip-btn gs-open"
        onClick={() => setOpen(true)}
        title={`${t(TX.open)} · Ctrl+K`}
        aria-label={t(TX.open)}
      >
        <span aria-hidden="true">⌕</span>
        <span className="gs-open-text">{t(TX.open)}</span>
        {/* Обозначение клавиш, а не подпись: Ctrl и K на любом языке пишутся
            одинаково, и «Упр K» не читается никем. */}
        <span className="gs-kbd" aria-hidden="true">{"Ctrl K"}</span>
      </button>

      {open && (
        <div className="gs-backdrop" onClick={close} role="presentation">
          <div
            className="gs-panel"
            role="dialog"
            aria-modal="true"
            aria-label={t(TX.open)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gs-head">
              <input
                ref={inputRef}
                className="gs-input"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t(TX.placeholder)}
                aria-label={t(TX.open)}
              />
              <span className="gs-keys">{t(TX.hintKeys)}</span>
            </div>

            <div className="gs-list" ref={listRef} role="listbox" aria-label={t(TX.open)}>
              {needle.length < 2 && <div className="gs-note">{t(TX.tooShort)}</div>}

              {needle.length >= 2 && (
                <>
                  {error !== null && <div className="gs-note gs-err">{error}</div>}

                  {items.length === 0 && !busy && (
                    <div className="gs-empty">
                      <div className="gs-empty-title">{t(TX.nothing)}</div>
                      <div className="gs-note">{t(TX.nothingHint)}</div>
                    </div>
                  )}

                  {groups.map(({ key, head }) => {
                    const rows = items.filter((it) => it.group === key);
                    if (rows.length === 0) return null;
                    return (
                      <div key={key} className="gs-group">
                        <div className="gs-group-head">{t(head)}</div>
                        {rows.map((it) => {
                          const at = items.indexOf(it);
                          /*
                           * Наведение мыши двигает курсор по `mousemove`, а не
                           * по `mouseenter`: при листании стрелками список
                           * прокручивается под неподвижной мышью, и
                           * `mouseenter` возвращал бы курсор на строку под
                           * указателем — стрелки перестали бы работать.
                           */
                          return (
                            <div
                              key={it.id}
                              className="gs-row"
                              role="option"
                              aria-selected={at === cursor ? "true" : "false"}
                              onMouseMove={() => setCursor(at)}
                              onClick={it.run}
                            >
                              <div className="gs-row-main">
                                <span className="gs-label">
                                  <Mark text={it.label} needle={needle} />
                                </span>
                                {it.badge !== null && (
                                  <span className={`ref-badge ${it.badge}`}>{t(BADGE_LABEL[it.badge])}</span>
                                )}
                                {it.inactive && <span className="tag">{t(TX.inactive)}</span>}
                                <span className="gs-kind">{t(it.kind)}</span>
                              </div>
                              <div className="gs-row-sub">
                                {it.alt !== null && (
                                  <span className="gs-alt">
                                    {t(TX.matchedOther)}: <Mark text={it.alt} needle={needle} />
                                  </span>
                                )}
                                {it.hint !== null && it.hint !== "" && <span>{it.hint}</span>}
                                <span className="gs-act">{t(it.action)}</span>
                              </div>
                            </div>
                          );
                        })}
                        {key === "entities" && moreLine !== "" && (
                          <div className="gs-note">{moreLine}</div>
                        )}
                      </div>
                    );
                  })}

                  {/*
                    Отсутствие раздела объясняется, а не замалчивается. Человек
                    без права `journey_lookup` иначе решит, что лида в системе
                    нет, — а он есть, просто не показывается. Молчание здесь
                    было бы не защитой данных, а ложью про данные.
                  */}
                  {server.records === null && <div className="gs-note">{t(TX.noRightNote)}</div>}
                  {server.records !== null && needle.length < server.minRecordQuery && (
                    <div className="gs-note">{t(TX.recordsNeedThree)}</div>
                  )}

                  {busy && <div className="gs-note">{t(TX.searching)}</div>}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Оформление держится в компоненте, а не в общем `theme.css`.
 *
 * Причина простая: файл темы общий, а поиск — одна самостоятельная деталь
 * интерфейса, и её правки не должны требовать входа в общий файл. Все цвета
 * берутся переменными темы, поэтому светлая и тёмная работают сами.
 */
const CSS = `
.gs-open { display: inline-flex; align-items: center; gap: 7px; }
.gs-open-text { font-size: var(--fs-sm); }
.gs-kbd {
  font-size: 10.5px; letter-spacing: .04em; padding: 1px 5px;
  border: 1px solid var(--line); border-radius: 5px; color: var(--ink-3);
}
.gs-backdrop {
  position: fixed; inset: 0; z-index: 80;
  background: rgba(3, 15, 34, 0.62);
  display: flex; justify-content: center; align-items: flex-start;
  padding: 8vh 16px 16px;
}
.gs-panel {
  width: min(720px, 100%); max-height: 76vh; display: flex; flex-direction: column;
  background: var(--card); color: var(--ink);
  border: 1px solid var(--line-2); border-radius: var(--radius);
  box-shadow: var(--flat); overflow: hidden;
}
.gs-head {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; border-bottom: 1px solid var(--line); background: var(--card);
}
.gs-input {
  flex: 1; background: transparent; border: 0; outline: 0;
  color: var(--ink); font: inherit; font-size: 16px;
}
.gs-keys { font-size: 11px; color: var(--ink-3); white-space: nowrap; }
.gs-list { overflow-y: auto; padding: 6px 0 10px; }
.gs-group { padding: 4px 0; }
.gs-group-head {
  padding: 8px 16px 4px; font-size: 11px; letter-spacing: .08em;
  text-transform: uppercase; color: var(--ink-3);
}
.gs-row { padding: 7px 16px; cursor: pointer; border-left: 3px solid transparent; }
.gs-row[aria-selected="true"] { background: var(--card-2); border-left-color: var(--accent); }
.gs-row-main { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.gs-label { font-weight: 600; }
.gs-kind { font-size: 11px; color: var(--ink-3); }
.gs-row-sub {
  display: flex; gap: 10px; flex-wrap: wrap;
  font-size: var(--fs-sm); color: var(--ink-3); margin-top: 1px;
}
.gs-act { color: var(--ink-2); }
.gs-alt { color: var(--ink-2); }
.gs-mark { background: var(--accent-soft); color: inherit; border-radius: 3px; padding: 0 1px; }
.gs-note { padding: 8px 16px; font-size: var(--fs-sm); color: var(--ink-3); }
.gs-err { color: var(--bad); }
.gs-empty { padding: 10px 0 4px; }
.gs-empty-title { padding: 0 16px; font-family: var(--f-display); font-size: 16px; }
`;
